import { z } from 'zod';

import { DAY_S, HOUR_S, MINUTE_S } from '@/lib/timeUnits';

import type { SettingsSnapshot } from './snapshot';

export const kLogLevels = [
    'none', 'error', 'warning', 'info', 'debug', 'verbose', 'trace',
] as const;

export const LogLevelSchema = z.enum(kLogLevels);
export type LogLevel = z.infer<typeof LogLevelSchema>;

// The document is sent back whole and the server replaces what it stores with it, so each level
// keeps the fields a newer server added.

export const LogTagFilterSchema = z.looseObject({
    tag: z.string(),
    level: LogLevelSchema,
});

export const LogLoggerConfigSchema = z.looseObject({
    name: z.string(),
    level: LogLevelSchema,
    filters: z.array(LogTagFilterSchema),
});

export const LogSettingsSchema = z.looseObject({
    maxFileSizeB: z.number(),
    maxVolumeSizeB: z.number(),
    maxFileTimePeriodS: z.number(),
    archivingEnabled: z.boolean(),
    loggers: z.array(LogLoggerConfigSchema),
    maxVolumeSizeLimitB: z.number(),
    source: z.enum(['file', 'defaults']),
});
export type LogSettings = z.infer<typeof LogSettingsSchema>;

export const LogSettingsDraftSchema = LogSettingsSchema.omit({
    maxVolumeSizeLimitB: true,
    source: true,
});
export type LogSettingsDraft = z.infer<typeof LogSettingsDraftSchema>;

export const LogComponentSchema = z.enum(['media_server', 'ai_agent_toolbox', 'vms_updater']);
export type LogComponent = z.infer<typeof LogComponentSchema>;

export const LogFileSchema = z.object({
    component: LogComponentSchema,
    name: z.string(),
    sizeB: z.number(),
    modifiedAt: z.iso.datetime(),
    // Only the answering server knows its own loggers, so the files of another component are
    // never live.
    live: z.boolean(),
    firstRecord: z.string().optional(),
});
export type LogFile = z.infer<typeof LogFileSchema>;

const LogFileKeySchema = z.object({
    component: z.string(),
    name: z.string(),
});

// A name repeats across components.
export function getLogFileKey(row: unknown): string | null {
    const key = LogFileKeySchema.safeParse(row);
    return key.success ? `${key.data.component}/${key.data.name}` : null;
}

export type LogSettingsSnapshot = SettingsSnapshot<LogSettings>;

export interface LogFilesSnapshot {
    files: LogFile[];
    isLoading: boolean;
    error: Error | null;
}

export interface LogFileContent {
    text: string;
    partial: boolean;
    totalB: number;
}

export const kViewLevels = [
    'ALWAYS', 'ERROR', 'WARNING', 'INFO', 'DEBUG', 'VERBOSE', 'TRACE',
] as const;

export type ViewLevel = (typeof kViewLevels)[number];

export interface LogRecord {
    // Stable across filtering, so that it can be the React key.
    index: number;
    timestamp: string;
    threadId: string;
    level: ViewLevel;
    tag: string;
    message: string;
    lines: string[];
}

export interface LogLine {
    record: LogRecord;
    lineIndex: number;
    text: string;
}

export const kDurationUnits = [
    { id: 'seconds', s: 1 },
    { id: 'minutes', s: MINUTE_S },
    { id: 'hours', s: HOUR_S },
    { id: 'days', s: DAY_S },
] as const;

export type DurationUnitId = (typeof kDurationUnits)[number]['id'];
